import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportXlsx } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Checkbox } from "@finance/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@finance/components/ui/popover";
import { DownloadSimple, FilePdf, MagnifyingGlass, FileText, Coins, Warning, CheckCircle, Clock, PenNib, Receipt, Columns, BookmarkSimple, FloppyDisk, X } from "@phosphor-icons/react";

const POSITIVE = new Set(["received", "paid", "signed", "confirmed", "delivered", "completed", "yes", "applicable", "dn signed"]);
const PENDING_WORDS = new Set(["not paid", "not received", "dn not signed", "not signed", "pending", "awaiting"]);
const isStatusWord = (v) => POSITIVE.has(v) || PENDING_WORDS.has(v);
const low = (x) => String(x || "").toLowerCase().trim();

// Leading columns to freeze (sticky): Stage · Client · Client PO.
const STICKY = [{ w: 120 }, { w: 170 }, { w: 130 }];
const stickyStyle = (idx, isHeader) => {
  if (idx >= STICKY.length) return {};
  const left = STICKY.slice(0, idx).reduce((a, c) => a + c.w, 0);
  return { position: "sticky", left, width: STICKY[idx].w, minWidth: STICKY[idx].w, zIndex: isHeader ? 30 : 20 };
};

const parseMoney = (v) => {
  const m = String(v || "").match(/^([A-Z]{3})\s+([\d,]+(?:\.\d+)?)/);
  return m ? { cur: m[1], amt: parseFloat(m[2].replace(/,/g, "")) } : null;
};
const sumMoney = (rows, key) => {
  const acc = {};
  rows.forEach((r) => { const p = parseMoney(r.v[key]); if (p) acc[p.cur] = (acc[p.cur] || 0) + p.amt; });
  return acc;
};
const fmtMoneyMap = (o) => Object.keys(o).length
  ? Object.entries(o).map(([c, a]) => `${c} ${a.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join(" · ")
  : "—";
const totalsRow = (rows) => ({ buy_ext: fmtMoneyMap(sumMoney(rows, "buy_ext")), sell_ext: fmtMoneyMap(sumMoney(rows, "sell_ext")) });

// Overall lifecycle stage for a line item, derived from its status fields.
const STAGE_STYLE = {
  Ordered: "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-700/40 dark:text-gray-200 dark:ring-gray-600",
  Received: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30",
  Delivered: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
  Invoiced: "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/30",
  Paid: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
};
function rowStage(v) {
  if (low(v.recv_status) === "received") return "Paid";
  if (v.lx_inv_no && low(v.lx_inv_no) !== "not received") return "Invoiced";
  if (low(v.dn_sign) === "dn signed") return "Delivered";
  if (low(v.pay_status) === "paid" || low(v.packing_list) === "received") return "Received";
  return "Ordered";
}
const Pill = ({ value, cls }) => <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ring-1 ${cls}`}>{value}</span>;
function StatusPill({ value, pending }) {
  const v = low(value);
  let cls = "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-700/40 dark:text-gray-200 dark:ring-gray-600";
  if (pending || PENDING_WORDS.has(v)) cls = "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30";
  else if (POSITIVE.has(v)) cls = "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30";
  return <Pill value={value} cls={cls} />;
}

function StatCard({ icon: Icon, label, value, tone = "text-gray-900 dark:text-gray-100", testid, onClick, active, clickable }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`rounded-lg border bg-white p-4 transition-all ${clickable ? "cursor-pointer" : ""} ${active ? "border-[#1B4383] ring-2 ring-[#1B4383]/30" : "border-gray-200 hover:shadow-md"}`}>
      <div className="flex items-center justify-between">
        <span className="lx-label">{label}</span>
        <Icon size={16} className={active ? "text-[#1B4383]" : "text-gray-300"} />
      </div>
      <div className={`mt-2 truncate font-display text-2xl font-bold lx-num ${tone}`}>{value}</div>
    </div>
  );
}

function ReportTable({ columns, rows, testid, navigate }) {
  const totals = totalsRow(rows);
  const [sel, setSel] = useState(null);
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm" data-testid={testid}>
      <table className="min-w-max text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((c, ci) => (
              <th key={c.key} style={stickyStyle(ci, true)} className="lx-label whitespace-nowrap bg-gray-50 px-3 py-3 text-left">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-gray-400">No records match this view.</td></tr>
          )}
          {rows.map((r, ri) => {
            const na = new Set(r.na || []);
            const red = new Set(r.red || []);
            const selected = sel === ri;
            const stickyBg = selected ? "bg-amber-50" : "bg-white";
            return (
              <tr key={ri} data-testid={`${testid}-row-${ri}`}
                onClick={() => setSel(ri)}
                onDoubleClick={() => r.tid && navigate(`/transactions/${r.tid}`)}
                title={r.tid ? "Double-click to open the PO card" : ""}
                className={`cursor-pointer border-b border-gray-100 transition-colors ${selected ? "bg-amber-50" : "hover:bg-gray-50/70"}`}>
                {columns.map((c, ci) => {
                  const sticky = ci < STICKY.length;
                  const cellBg = selected ? "bg-amber-50" : sticky ? "bg-white" : "";
                  if (c.key === "__stage") {
                    const st = rowStage(r.v);
                    return <td key={c.key} style={stickyStyle(ci)} className={`px-3 py-2.5 ${stickyBg}`} data-testid={`${testid}-${ri}-stage`}><Pill value={st} cls={STAGE_STYLE[st]} /></td>;
                  }
                  if (na.has(c.key)) {
                    return <td key={c.key} style={stickyStyle(ci)} data-testid={`${testid}-${ri}-${c.key}-na`} className={`px-3 py-2.5 ${cellBg}`}>
                      <Pill value="N/A" cls="bg-gray-100 text-gray-400 ring-gray-200 dark:bg-gray-700/40 dark:text-gray-400 dark:ring-gray-600" />
                    </td>;
                  }
                  const val = r.v[c.key] ?? "";
                  const vl = low(val);
                  const isRed = red.has(c.key) || PENDING_WORDS.has(vl);
                  const asPill = !!val && (isRed || isStatusWord(vl));
                  return (
                    <td key={c.key} style={stickyStyle(ci)} data-testid={`${testid}-${ri}-${c.key}`}
                      className={`whitespace-nowrap px-3 py-2.5 ${cellBg} ${ci <= 1 ? "font-medium text-gray-800 dark:text-gray-100" : "font-normal text-gray-600"}`}>
                      {asPill ? <StatusPill value={val} pending={isRed} /> : (val || "—")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {rows.length > 0 && (
            <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800 dark:text-gray-100" data-testid={`${testid}-totals`}>
              {columns.map((c, ci) => (
                <td key={c.key} style={stickyStyle(ci)} className={`whitespace-nowrap px-3 py-2.5 ${ci < STICKY.length ? "bg-gray-50" : ""}`}>
                  {ci === 0 ? `TOTAL · ${rows.length}` : (totals[c.key] || "")}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const SEGMENTS = [["all", "All"], ["pending", "Pending"], ["completed", "Completed"]];
const STAGE_COL = { key: "__stage", label: "Stage" };
const CARD_FILTERS = {
  pending: (r) => (r.red || []).length > 0,
  completed: (r) => !(r.red || []).length,
  awaiting: (r) => { const s = low(r.v.recv_status); return s && s !== "received"; },
  dn: (r) => low(r.v.dn_sign) === "dn not signed",
  uninvoiced: (r) => { const i = r.v.lx_inv_no; return !i || low(i) === "not received"; },
};

export default function EndToEnd({ only }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [seg, setSeg] = useState("all");
  const [q, setQ] = useState("");
  const COLS_KEY = `lfm_e2e_cols_${only || "local"}`;
  const [hidden, setHidden] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(COLS_KEY) || "[]")); } catch { return new Set(); } });
  const applyHidden = (n) => { setHidden(n); localStorage.setItem(COLS_KEY, JSON.stringify([...n])); };
  const [cardFilter, setCardFilter] = useState(null);
  const VIEWS_KEY = `lfm_e2e_views_${only || "local"}`;
  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem(VIEWS_KEY) || "[]"); } catch { return []; } });
  const [viewName, setViewName] = useState("");

  const load = useCallback(() => api.get("/reports/spreadsheet").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const section = data ? (only === "import" ? data.import : data.local) : null;
  const isImport = only === "import";
  const label = isImport ? "Import" : "Local";
  const allRows = useMemo(() => section?.rows || [], [section]);
  const baseCols = section?.columns || [];
  const alwaysKeys = useMemo(() => new Set([baseCols[0]?.key, baseCols[1]?.key]), [baseCols]);
  const renderCols = useMemo(() => [STAGE_COL, ...baseCols.filter((c) => !hidden.has(c.key))], [baseCols, hidden]);

  const stats = useMemo(() => {
    const pos = new Set(allRows.map((r) => r.v.lx_po_no).filter(Boolean)).size;
    const buy = fmtMoneyMap(sumMoney(allRows, "buy_ext"));
    const pending = allRows.filter((r) => (r.red || []).length).length;
    const cnt = (fn) => allRows.filter(fn).length;
    if (isImport) {
      return [
        { icon: FileText, label: "Total Somcore POs", value: pos, testid: "e2e-stat-pos" },
        { icon: Coins, label: "Total Import Value", value: buy, tone: "text-[#1B4383]", testid: "e2e-stat-value" },
        { icon: Warning, label: "Pending Rows", value: pending, tone: pending ? "text-amber-600" : "", testid: "e2e-stat-pending", fkey: "pending" },
        { icon: CheckCircle, label: "Completed Rows", value: allRows.length - pending, tone: "text-emerald-700", testid: "e2e-stat-completed", fkey: "completed" },
      ];
    }
    const awaitingPay = cnt((r) => { const s = low(r.v.recv_status); return s && s !== "received"; });
    const dnUnsigned = cnt((r) => low(r.v.dn_sign) === "dn not signed");
    const uninvoiced = cnt((r) => { const i = r.v.lx_inv_no; return !i || low(i) === "not received"; });
    return [
      { icon: FileText, label: "Total Somcore POs", value: pos, testid: "e2e-stat-pos" },
      { icon: Coins, label: "Total Order Value", value: buy, tone: "text-[#1B4383]", testid: "e2e-stat-value" },
      { icon: Clock, label: "Awaiting Payment", value: awaitingPay, tone: awaitingPay ? "text-amber-600" : "", testid: "e2e-stat-awaiting", fkey: "awaiting" },
      { icon: PenNib, label: "DN Unsigned", value: dnUnsigned, tone: dnUnsigned ? "text-red-600" : "", testid: "e2e-stat-dn", fkey: "dn" },
      { icon: Receipt, label: "Uninvoiced", value: uninvoiced, tone: uninvoiced ? "text-amber-600" : "", testid: "e2e-stat-uninvoiced", fkey: "uninvoiced" },
    ];
  }, [allRows, isImport]);

  const rows = useMemo(() => allRows.filter((r) => {
    const pendingRow = (r.red || []).length > 0;
    if (seg === "pending" && !pendingRow) return false;
    if (seg === "completed" && pendingRow) return false;
    if (cardFilter && CARD_FILTERS[cardFilter] && !CARD_FILTERS[cardFilter](r)) return false;
    if (q) {
      const hay = [r.v.client_name, r.v.lx_po_no, r.v.client_po_no, r.v.supplier_name, r.v.item_name].join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [allRows, seg, q, cardFilter]);

  const segCount = (k) => k === "all" ? allRows.length : allRows.filter((r) => k === "pending" ? (r.red || []).length : !(r.red || []).length).length;
  const persistViews = (v) => { setViews(v); localStorage.setItem(VIEWS_KEY, JSON.stringify(v)); };
  const saveView = () => { if (!viewName.trim()) return; persistViews([...views.filter((x) => x.name !== viewName.trim()), { name: viewName.trim(), q, seg }]); setViewName(""); };

  const exportExcel = () => {
    if (!section) return;
    const cols = renderCols.filter((c) => c.key !== "__stage").map((c) => ({ key: c.key, label: c.label }));
    const out = rows.map((r) => {
      const o = {};
      cols.forEach((c) => { o[c.key] = (r.na || []).includes(c.key) ? "N/A" : (r.v[c.key] ?? ""); });
      return o;
    });
    exportXlsx(`end-to-end-${label}`, out, cols);
  };

  return (
    <Layout>
      <PageHeader
        title={`End-to-End — ${label}`}
        subtitle={`Full PO lifecycle for ${label.toUpperCase()} transactions — one row per line item`}
        actions={section && (
          <>
            <Button variant="outline" data-testid="e2e-export-excel" onClick={exportExcel}><DownloadSimple size={16} className="mr-1" />Excel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="e2e-export-pdf" onClick={() => openPdf("pdf/spreadsheet-report")}><FilePdf size={16} className="mr-1" />PDF</Button>
          </>
        )}
      />
      <div className="space-y-6 p-8">
        {!section ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">Loading report…</div>
        ) : (
          <>
            <div className={`grid grid-cols-2 gap-4 ${isImport ? "lg:grid-cols-4" : "sm:grid-cols-3 xl:grid-cols-5"}`} data-testid="e2e-stats">
              {stats.map((s) => <StatCard key={s.testid} {...s} clickable={!!s.fkey} active={s.fkey && cardFilter === s.fkey} onClick={() => setCardFilter(s.fkey && cardFilter !== s.fkey ? s.fkey : null)} />)}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs font-medium">
                {SEGMENTS.map(([k, lbl]) => (
                  <button key={k} data-testid={`e2e-seg-${k}`} onClick={() => setSeg(k)}
                    className={`rounded-md px-3 py-1.5 transition-colors ${seg === k ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900" : "text-gray-500 hover:text-gray-900 dark:text-gray-400"}`}>
                    {lbl} <span className="opacity-60">({segCount(k)})</span>
                  </button>
                ))}
              </div>
              <div className="relative w-full max-w-xs">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="h-9 pl-8" placeholder="Search client, PO or supplier…" data-testid="e2e-search" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>

              {/* Column chooser */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="e2e-columns-btn"><Columns size={15} className="mr-1" />Columns</Button>
                </PopoverTrigger>
                <PopoverContent className="max-h-80 w-64 overflow-auto p-2" align="end">
                  <div className="lx-label mb-1 px-1">Show columns</div>
                  {baseCols.map((c) => alwaysKeys.has(c.key) ? null : (
                    <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700/40" data-testid={`e2e-col-${c.key}`}>
                      <Checkbox checked={!hidden.has(c.key)} onCheckedChange={(ck) => { const n = new Set(hidden); ck ? n.delete(c.key) : n.add(c.key); applyHidden(n); }} />
                      {c.label}
                    </label>
                  ))}
                </PopoverContent>
              </Popover>

              {/* Saved views */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="e2e-views-btn"><BookmarkSimple size={15} className="mr-1" />Views{views.length ? ` (${views.length})` : ""}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2" align="end">
                  <div className="lx-label mb-1 px-1">Saved views</div>
                  {views.length === 0 && <div className="px-2 py-1 text-xs text-gray-400">No saved views yet.</div>}
                  {views.map((v) => (
                    <div key={v.name} className="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <button className="flex-1 text-left" data-testid={`e2e-view-${v.name}`} onClick={() => { setQ(v.q || ""); setSeg(v.seg || "all"); }}>
                        {v.name} <span className="text-gray-400">· {v.seg}{v.q ? ` · "${v.q}"` : ""}</span>
                      </button>
                      <button className="text-gray-400 hover:text-red-500" onClick={() => persistViews(views.filter((x) => x.name !== v.name))}><X size={13} /></button>
                    </div>
                  ))}
                  <div className="mt-2 flex items-center gap-1 border-t border-gray-100 pt-2">
                    <Input className="h-8 text-xs" placeholder="Name this view…" data-testid="e2e-view-name" value={viewName} onChange={(e) => setViewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveView()} />
                    <Button size="sm" className="h-8" data-testid="e2e-view-save" onClick={saveView}><FloppyDisk size={14} className="mr-1" />Save</Button>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="ml-auto flex flex-wrap items-center gap-4 text-[11px] text-gray-400">
                <span className="inline-flex items-center gap-1"><StatusPill value="Paid" /> Complete</span>
                <span className="inline-flex items-center gap-1"><StatusPill value="Not received" pending /> Pending</span>
                <span>Double-click a row to open the PO card</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="e2e-stage-legend">
              <span className="font-medium text-gray-500">Lifecycle:</span>
              {["Ordered", "Received", "Delivered", "Invoiced", "Paid"].map((s) => <Pill key={s} value={s} cls={STAGE_STYLE[s]} />)}
              {cardFilter && <button className="ml-2 text-gray-400 underline hover:text-gray-700" data-testid="e2e-clear-filter" onClick={() => setCardFilter(null)}>Clear card filter</button>}
            </div>

            <ReportTable columns={renderCols} rows={rows} testid={`e2e-${only || "local"}`} navigate={navigate} />
          </>
        )}
      </div>
    </Layout>
  );
}
